import { useState, useMemo } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type {
  ValidationResult,
  ValidatedProduct,
  ValidatedRawMaterial,
  ValidatedModifier,
  ValidatedCategory,
  ValidationError,
} from '@/lib/import';

type FilterType = 'all' | 'errors' | 'warnings' | 'valid';

interface ImportPreviewProps {
  validationResult: ValidationResult;
  onRemoveRow?: (type: 'products' | 'rawMaterials' | 'modifiers' | 'categories', row: number) => void;
}

export function ImportPreview({ validationResult, onRemoveRow }: ImportPreviewProps) {
  const [activeTab, setActiveTab] = useState<string>('products');
  const [filter, setFilter] = useState<FilterType>('all');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { products, rawMaterials, modifiers, categories, summary } = validationResult;

  // Determine which tabs to show
  const tabs = useMemo(() => {
    const result: Array<{ id: string; label: string; count: number; errorCount: number }> = [];
    if (products.length > 0) {
      result.push({
        id: 'products',
        label: 'Products',
        count: products.length,
        errorCount: summary.products.errors,
      });
    }
    if (rawMaterials.length > 0) {
      result.push({
        id: 'rawMaterials',
        label: 'Raw Materials',
        count: rawMaterials.length,
        errorCount: summary.rawMaterials.errors,
      });
    }
    if (modifiers.length > 0) {
      result.push({
        id: 'modifiers',
        label: 'Modifiers',
        count: modifiers.length,
        errorCount: summary.modifiers.errors,
      });
    }
    if (categories.length > 0) {
      result.push({
        id: 'categories',
        label: 'Categories',
        count: categories.length,
        errorCount: summary.categories.errors,
      });
    }
    return result;
  }, [products, rawMaterials, modifiers, categories, summary]);

  // Filter function
  const filterItems = <T extends { errors: ValidationError[]; isValid: boolean }>(items: T[]): T[] => {
    switch (filter) {
      case 'errors':
        return items.filter(item => item.errors.some(e => e.severity === 'error'));
      case 'warnings':
        return items.filter(item => item.errors.some(e => e.severity === 'warning') && item.isValid);
      case 'valid':
        return items.filter(item => item.isValid && item.errors.length === 0);
      default:
        return items;
    }
  };

  const toggleRowExpanded = (rowKey: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowKey)) {
        next.delete(rowKey);
      } else {
        next.add(rowKey);
      }
      return next;
    });
  };

  const getStatusIcon = (errors: ValidationError[], _isValid: boolean) => {
    const hasErrors = errors.some(e => e.severity === 'error');
    const hasWarnings = errors.some(e => e.severity === 'warning');

    if (hasErrors) {
      return <XCircle className="h-4 w-4 text-destructive" />;
    }
    if (hasWarnings) {
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  };

  const getActionBadge = (action: 'create' | 'update') => {
    if (action === 'update') {
      return <Badge variant="secondary" className="text-xs">Update</Badge>;
    }
    return <Badge variant="outline" className="text-xs">New</Badge>;
  };

  // Auto-select first available tab
  const effectiveTab = tabs.find(t => t.id === activeTab) ? activeTab : tabs[0]?.id || 'products';

  if (tabs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No data found in the imported file
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-3 bg-muted/50 rounded-lg">
          <div className="text-2xl font-bold">{products.length + rawMaterials.length + categories.length}</div>
          <div className="text-xs text-muted-foreground">Total Items</div>
        </div>
        <div className="p-3 bg-green-500/10 rounded-lg">
          <div className="text-2xl font-bold text-green-600">
            {summary.products.valid + summary.rawMaterials.valid + summary.categories.valid}
          </div>
          <div className="text-xs text-muted-foreground">Valid</div>
        </div>
        <div className="p-3 bg-destructive/10 rounded-lg">
          <div className="text-2xl font-bold text-destructive">{summary.totalErrors}</div>
          <div className="text-xs text-muted-foreground">Errors</div>
        </div>
        <div className="p-3 bg-yellow-500/10 rounded-lg">
          <div className="text-2xl font-bold text-yellow-600">{summary.totalWarnings}</div>
          <div className="text-xs text-muted-foreground">Warnings</div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2">
        {(['all', 'errors', 'warnings', 'valid'] as FilterType[]).map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={effectiveTab} onValueChange={setActiveTab}>
        <TabsList>
          {tabs.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id} className="relative">
              {tab.label}
              <span className="ml-2 text-xs text-muted-foreground">({tab.count})</span>
              {tab.errorCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-[10px] text-destructive-foreground flex items-center justify-center">
                  {tab.errorCount}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Products Tab */}
        <TabsContent value="products" className="mt-4">
          <ProductsTable
            products={filterItems(products)}
            expandedRows={expandedRows}
            onToggleExpand={toggleRowExpanded}
            getStatusIcon={getStatusIcon}
            getActionBadge={getActionBadge}
            onRemoveRow={onRemoveRow ? (row) => onRemoveRow('products', row) : undefined}
          />
        </TabsContent>

        {/* Raw Materials Tab */}
        <TabsContent value="rawMaterials" className="mt-4">
          <RawMaterialsTable
            materials={filterItems(rawMaterials)}
            expandedRows={expandedRows}
            onToggleExpand={toggleRowExpanded}
            getStatusIcon={getStatusIcon}
            getActionBadge={getActionBadge}
            onRemoveRow={onRemoveRow ? (row) => onRemoveRow('rawMaterials', row) : undefined}
          />
        </TabsContent>

        {/* Modifiers Tab */}
        <TabsContent value="modifiers" className="mt-4">
          <ModifiersTable
            modifiers={filterItems(modifiers)}
            expandedRows={expandedRows}
            onToggleExpand={toggleRowExpanded}
            getStatusIcon={getStatusIcon}
            onRemoveRow={onRemoveRow ? (row) => onRemoveRow('modifiers', row) : undefined}
          />
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-4">
          <CategoriesTable
            categories={filterItems(categories)}
            expandedRows={expandedRows}
            onToggleExpand={toggleRowExpanded}
            getStatusIcon={getStatusIcon}
            getActionBadge={getActionBadge}
            onRemoveRow={onRemoveRow ? (row) => onRemoveRow('categories', row) : undefined}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Products Table Component
interface ProductsTableProps {
  products: ValidatedProduct[];
  expandedRows: Set<string>;
  onToggleExpand: (key: string) => void;
  getStatusIcon: (errors: ValidationError[], isValid: boolean) => React.ReactNode;
  getActionBadge: (action: 'create' | 'update') => React.ReactNode;
  onRemoveRow?: (row: number) => void;
}

function ProductsTable({
  products,
  expandedRows,
  onToggleExpand,
  getStatusIcon,
  getActionBadge,
}: ProductsTableProps) {
  if (products.length === 0) {
    return <div className="text-center py-4 text-muted-foreground">No products match the current filter</div>;
  }

  return (
    <div className="border rounded-lg">
      <div className="overflow-auto max-h-[400px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-12">Status</TableHead>
              <TableHead className="w-12">Row</TableHead>
              <TableHead className="min-w-[120px]">Name</TableHead>
              <TableHead className="min-w-[80px]">SKU</TableHead>
              <TableHead className="text-right min-w-[70px]">Price</TableHead>
              <TableHead className="min-w-[80px]">Category</TableHead>
              <TableHead className="w-16">Action</TableHead>
            </TableRow>
          </TableHeader>
        <TableBody>
          {products.map(product => {
            const rowKey = `product-${product.row}`;
            const isExpanded = expandedRows.has(rowKey);
            const hasMessages = product.errors.length > 0;

            return (
              <>
                <TableRow
                  key={rowKey}
                  className={cn(
                    'cursor-pointer',
                    !product.isValid && 'bg-destructive/5'
                  )}
                  onClick={() => hasMessages && onToggleExpand(rowKey)}
                >
                  <TableCell>
                    {hasMessages && (
                      isExpanded
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />
                    )}
                  </TableCell>
                  <TableCell>{getStatusIcon(product.errors, product.isValid)}</TableCell>
                  <TableCell className="text-muted-foreground">{product.row}</TableCell>
                  <TableCell className="font-medium">{product.name || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{product.sku || '-'}</TableCell>
                  <TableCell className="text-right">
                    {product.price !== undefined ? `$${product.price.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>{product.category || '-'}</TableCell>
                  <TableCell>{getActionBadge(product.action)}</TableCell>
                </TableRow>
                {isExpanded && hasMessages && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-muted/30 p-0">
                      <div className="p-3 space-y-1">
                        {product.errors.map((error, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center gap-2 text-sm',
                              error.severity === 'error' ? 'text-destructive' : 'text-yellow-600'
                            )}
                          >
                            {error.severity === 'error'
                              ? <XCircle className="h-3 w-3" />
                              : <AlertTriangle className="h-3 w-3" />
                            }
                            <span className="font-medium">{error.field}:</span>
                            <span>{error.message}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Raw Materials Table Component
interface RawMaterialsTableProps {
  materials: ValidatedRawMaterial[];
  expandedRows: Set<string>;
  onToggleExpand: (key: string) => void;
  getStatusIcon: (errors: ValidationError[], isValid: boolean) => React.ReactNode;
  getActionBadge: (action: 'create' | 'update') => React.ReactNode;
  onRemoveRow?: (row: number) => void;
}

function RawMaterialsTable({
  materials,
  expandedRows,
  onToggleExpand,
  getStatusIcon,
  getActionBadge,
}: RawMaterialsTableProps) {
  if (materials.length === 0) {
    return <div className="text-center py-4 text-muted-foreground">No raw materials match the current filter</div>;
  }

  return (
    <div className="border rounded-lg">
      <div className="overflow-auto max-h-[400px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-12">Status</TableHead>
              <TableHead className="w-12">Row</TableHead>
              <TableHead className="min-w-[120px]">Name</TableHead>
              <TableHead className="min-w-[70px]">SKU</TableHead>
              <TableHead className="min-w-[60px]">Unit</TableHead>
              <TableHead className="text-right min-w-[70px]">Cost</TableHead>
              <TableHead className="min-w-[100px]">Supplier</TableHead>
              <TableHead className="w-16">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {materials.map(material => {
            const rowKey = `material-${material.row}`;
            const isExpanded = expandedRows.has(rowKey);
            const hasMessages = material.errors.length > 0;

            return (
              <>
                <TableRow
                  key={rowKey}
                  className={cn(
                    'cursor-pointer',
                    !material.isValid && 'bg-destructive/5'
                  )}
                  onClick={() => hasMessages && onToggleExpand(rowKey)}
                >
                  <TableCell>
                    {hasMessages && (
                      isExpanded
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />
                    )}
                  </TableCell>
                  <TableCell>{getStatusIcon(material.errors, material.isValid)}</TableCell>
                  <TableCell className="text-muted-foreground">{material.row}</TableCell>
                  <TableCell className="font-medium">{material.name || '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{material.sku || '-'}</TableCell>
                  <TableCell>{material.unit_of_measure || '-'}</TableCell>
                  <TableCell className="text-right">
                    {material.cost_per_unit !== undefined ? `$${material.cost_per_unit.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell>{material.supplier_name || '-'}</TableCell>
                  <TableCell>{getActionBadge(material.action)}</TableCell>
                </TableRow>
                {isExpanded && hasMessages && (
                  <TableRow>
                    <TableCell colSpan={9} className="bg-muted/30 p-0">
                      <div className="p-3 space-y-1">
                        {material.errors.map((error, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center gap-2 text-sm',
                              error.severity === 'error' ? 'text-destructive' : 'text-yellow-600'
                            )}
                          >
                            {error.severity === 'error'
                              ? <XCircle className="h-3 w-3" />
                              : <AlertTriangle className="h-3 w-3" />
                            }
                            <span className="font-medium">{error.field}:</span>
                            <span>{error.message}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Modifiers Table Component
interface ModifiersTableProps {
  modifiers: ValidatedModifier[];
  expandedRows: Set<string>;
  onToggleExpand: (key: string) => void;
  getStatusIcon: (errors: ValidationError[], isValid: boolean) => React.ReactNode;
  onRemoveRow?: (row: number) => void;
}

function ModifiersTable({
  modifiers,
  expandedRows,
  onToggleExpand,
  getStatusIcon,
}: ModifiersTableProps) {
  if (modifiers.length === 0) {
    return <div className="text-center py-4 text-muted-foreground">No modifiers match the current filter</div>;
  }

  return (
    <div className="border rounded-lg">
      <div className="overflow-auto max-h-[400px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-12">Status</TableHead>
              <TableHead className="w-12">Row</TableHead>
              <TableHead className="min-w-[100px]">Group</TableHead>
              <TableHead className="min-w-[100px]">Option</TableHead>
              <TableHead className="text-right min-w-[80px]">Price Adj.</TableHead>
              <TableHead className="min-w-[70px]">Required</TableHead>
              <TableHead className="min-w-[90px]">Selections</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {modifiers.map(modifier => {
            const rowKey = `modifier-${modifier.row}`;
            const isExpanded = expandedRows.has(rowKey);
            const hasMessages = modifier.errors.length > 0;

            return (
              <>
                <TableRow
                  key={rowKey}
                  className={cn(
                    'cursor-pointer',
                    !modifier.isValid && 'bg-destructive/5'
                  )}
                  onClick={() => hasMessages && onToggleExpand(rowKey)}
                >
                  <TableCell>
                    {hasMessages && (
                      isExpanded
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />
                    )}
                  </TableCell>
                  <TableCell>{getStatusIcon(modifier.errors, modifier.isValid)}</TableCell>
                  <TableCell className="text-muted-foreground">{modifier.row}</TableCell>
                  <TableCell className="font-medium">{modifier.group_name || '-'}</TableCell>
                  <TableCell>{modifier.option_name || '-'}</TableCell>
                  <TableCell className="text-right">
                    {modifier.price_adjustment !== undefined
                      ? (modifier.price_adjustment >= 0 ? '+' : '') + `$${modifier.price_adjustment.toFixed(2)}`
                      : '-'
                    }
                  </TableCell>
                  <TableCell>{modifier.is_required ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {modifier.min_selections !== undefined || modifier.max_selections !== undefined
                      ? `${modifier.min_selections ?? 0} - ${modifier.max_selections ?? 'unlimited'}`
                      : '-'
                    }
                  </TableCell>
                </TableRow>
                {isExpanded && hasMessages && (
                  <TableRow>
                    <TableCell colSpan={8} className="bg-muted/30 p-0">
                      <div className="p-3 space-y-1">
                        {modifier.errors.map((error, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center gap-2 text-sm',
                              error.severity === 'error' ? 'text-destructive' : 'text-yellow-600'
                            )}
                          >
                            {error.severity === 'error'
                              ? <XCircle className="h-3 w-3" />
                              : <AlertTriangle className="h-3 w-3" />
                            }
                            <span className="font-medium">{error.field}:</span>
                            <span>{error.message}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Categories Table Component
interface CategoriesTableProps {
  categories: ValidatedCategory[];
  expandedRows: Set<string>;
  onToggleExpand: (key: string) => void;
  getStatusIcon: (errors: ValidationError[], isValid: boolean) => React.ReactNode;
  getActionBadge: (action: 'create' | 'update') => React.ReactNode;
  onRemoveRow?: (row: number) => void;
}

function CategoriesTable({
  categories,
  expandedRows,
  onToggleExpand,
  getStatusIcon,
  getActionBadge,
}: CategoriesTableProps) {
  if (categories.length === 0) {
    return <div className="text-center py-4 text-muted-foreground">No categories match the current filter</div>;
  }

  return (
    <div className="border rounded-lg">
      <div className="overflow-auto max-h-[400px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead className="w-12">Status</TableHead>
              <TableHead className="w-12">Row</TableHead>
              <TableHead className="min-w-[120px]">Name</TableHead>
              <TableHead className="min-w-[150px]">Description</TableHead>
              <TableHead className="min-w-[100px]">Parent</TableHead>
              <TableHead className="w-16">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map(category => {
            const rowKey = `category-${category.row}`;
            const isExpanded = expandedRows.has(rowKey);
            const hasMessages = category.errors.length > 0;

            return (
              <>
                <TableRow
                  key={rowKey}
                  className={cn(
                    'cursor-pointer',
                    !category.isValid && 'bg-destructive/5'
                  )}
                  onClick={() => hasMessages && onToggleExpand(rowKey)}
                >
                  <TableCell>
                    {hasMessages && (
                      isExpanded
                        ? <ChevronDown className="h-4 w-4" />
                        : <ChevronRight className="h-4 w-4" />
                    )}
                  </TableCell>
                  <TableCell>{getStatusIcon(category.errors, category.isValid)}</TableCell>
                  <TableCell className="text-muted-foreground">{category.row}</TableCell>
                  <TableCell className="font-medium">{category.name || '-'}</TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-[200px]">
                    {category.description || '-'}
                  </TableCell>
                  <TableCell>{category.parent_category || '-'}</TableCell>
                  <TableCell>{getActionBadge(category.action)}</TableCell>
                </TableRow>
                {isExpanded && hasMessages && (
                  <TableRow>
                    <TableCell colSpan={7} className="bg-muted/30 p-0">
                      <div className="p-3 space-y-1">
                        {category.errors.map((error, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex items-center gap-2 text-sm',
                              error.severity === 'error' ? 'text-destructive' : 'text-yellow-600'
                            )}
                          >
                            {error.severity === 'error'
                              ? <XCircle className="h-3 w-3" />
                              : <AlertTriangle className="h-3 w-3" />
                            }
                            <span className="font-medium">{error.field}:</span>
                            <span>{error.message}</span>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
